// Bridges the SPA to the Payment Handler SW when the SPA is the payment window
// (opened with ?c=1). Posts one airkey-pay/1 message per terminal outcome, and
// the SW maps it to the PaymentResponse. See public/payment-handler.js.

export const PROTOCOL_VERSION = 'airkey-pay/1';

// PaymentResponse.details.code reported when Web NFC permission is not yet granted
// for this origin. A Payment Handler window (Chrome 149+) can use an existing grant
// but never prompt for one, so this is the signal the invoker uses to kick out to the
// enable-NFC interstitial. Rides the PAYMENT_ERROR passthrough (the SW forwards it
// verbatim; see public/payment-handler-logic.js).
export const NFC_PERMISSION_NEEDED_CODE = 'NFC_PERMISSION_NEEDED';

// Dedicated top-level route for the NFC permission page. The invoker opens
// `<origin>/enable-nfc` as a REAL top-level tab (not a PH window, outside the /3ds and
// /ul flows), where the NFC prompt can actually appear. Registered in src/Routes.tsx;
// kept in sync with the hardcoded string in payment-harness/ (plain JS, can't import this).
export const ENABLE_NFC_PATH = '/enable-nfc';

// postMessage type the enable-NFC page posts back to window.opener (the invoker) once
// permission is granted or the user cancels, just before it auto-closes. Mirrored in
// the harness.
export const NFC_ENABLE_RESULT_TYPE = 'NFC_ENABLE_RESULT';

// Query params the invoker adds when opening the enable-NFC tab, so each window
// message can be validated against a unique operation id and a known opener origin
// (not trusting '*'): the page echoes `op` back and targets its result at
// `openerOrigin`. Absent when the page is opened directly. Mirrored in the harness.
export const ENABLE_NFC_OP_PARAM = 'op';
export const ENABLE_NFC_OPENER_PARAM = 'openerOrigin';

/** True when the SPA is running inside the payment-handler window (?c=1). */
export function isPaymentHandlerWindow(
  search: string = typeof window === 'undefined' ? '' : window.location.search,
): boolean {
  return new URLSearchParams(search).get('c') === '1';
}

// Best-effort post to the controlling SW. Prefers getRegistration(): inside a Payment
// Handler window `serviceWorker.ready` never resolves (the SW is active but was not
// registered from this document), so it would hang and the signal would never fire.
// getRegistration() returns the active worker immediately; .ready stays as the
// fallback for ordinary windows. (.controller is null on a freshly opened window.)
// If the SW is gone, the invoker times out.
async function postToServiceWorker(
  message: Record<string, unknown>,
): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }
  const sw = navigator.serviceWorker;
  try {
    let registration =
      typeof sw.getRegistration === 'function'
        ? await sw.getRegistration()
        : undefined;
    if (!registration?.active) {
      registration = await sw.ready;
    }
    registration?.active?.postMessage(message);
  } catch {
    // SW unreachable — the invoker times out.
  }
}

export function postPaymentComplete(
  result: Record<string, unknown>,
): Promise<void> {
  return postToServiceWorker({
    protocol: PROTOCOL_VERSION,
    type: 'PAYMENT_COMPLETE',
    result,
  });
}

export function postPaymentCancelled(): Promise<void> {
  return postToServiceWorker({
    protocol: PROTOCOL_VERSION,
    type: 'PAYMENT_CANCELLED',
  });
}

export function postPaymentError(code: string, error?: string): Promise<void> {
  return postToServiceWorker({
    protocol: PROTOCOL_VERSION,
    type: 'PAYMENT_ERROR',
    code,
    error,
  });
}

/**
 * Signals the invoker that NFC permission is not granted, so the tap cannot complete
 * in the Payment Handler window. Rides the PAYMENT_ERROR passthrough with
 * NFC_PERMISSION_NEEDED_CODE (see above); the invoker then kicks out to the enable-NFC
 * interstitial and re-presents a fresh PH window. See docs/PAYMENT_HANDLER.md.
 */
export function postNfcPermissionNeeded(error?: string): Promise<void> {
  return postPaymentError(
    NFC_PERMISSION_NEEDED_CODE,
    error ?? 'NFC permission has not been granted for this origin',
  );
}

export interface TerminalOutcome {
  success?: boolean;
  cancelled?: boolean;
  verificationFailed?: boolean;
  errorType?: string | null;
  policyProcessId?: string;
}

// Posts for the first terminal state present, in priority order. Returns the
// type posted, or null if none set, so the caller effect is harmless each render.
export function reportTerminalOutcome(outcome: TerminalOutcome): string | null {
  if (outcome.success) {
    postPaymentComplete({
      success: true,
      policyProcessId: outcome.policyProcessId,
    });
    return 'PAYMENT_COMPLETE';
  }
  if (outcome.cancelled) {
    postPaymentCancelled();
    return 'PAYMENT_CANCELLED';
  }
  if (outcome.verificationFailed) {
    postPaymentError('VERIFICATION_FAILED', 'Verification failed');
    return 'PAYMENT_ERROR';
  }
  if (outcome.errorType) {
    postPaymentError('INTERNAL', outcome.errorType);
    return 'PAYMENT_ERROR';
  }
  return null;
}
